import { type ChainId, ChainIds, type MultichainTokenBalance } from "@umami/chains";

import { type BridgeNetwork, CHAIN_ID_MAPPING } from "../config";
import { PRICE_SERVICE_URL } from "../env";

// Custom error for quote-related errors
export class QuoteError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "QuoteError";
  }
}

export interface FirmQuoteResponse {
  blockNumber: string;
  buyAmount: string;
  buyToken: string;
  sellAmount: string;
  sellToken: string;
  totalNetworkFee?: string;
  fees?: {
    integratorFee: {
      amount: string;
      token: string;
      type: string;
    } | null;
    zeroExFee: {
      amount: string;
      token: string;
      type: string;
    } | null;
    gasFee: {
      amount: string;
      token: string;
      type: string;
    } | null;
  };
  route: {
    fills: Array<{
      from: string;
      to: string;
      source: string;
      proportionBps: string;
    }>;
    tokens: Array<{
      address: string;
      symbol: string;
    }>;
  };
  issues?: {
    allowance?: {
      actual: string;
      spender: string;
    };
    balance: {
      token: string;
      actual: string;
      expected: string;
    };
    simulationIncomplete: boolean;
    invalidSourcesPassed: string[];
  };
  liquidityAvailable: boolean;
  minBuyAmount: string;
  transaction?: {
    to: string;
    data: string;
    gas: string;
    gasPrice: string;
    value: string;
  };
  permit2?: {
    eip712: {
      types: Record<string, any[]>;
      domain: Record<string, any>;
      message: Record<string, any>;
      primaryType: string;
    };
  };
  approval?: {
    type: string;
    hash: string;
    eip712: {
      types: Record<string, any[]>;
      domain: Record<string, any>;
      message: Record<string, any>;
      primaryType: string;
    };
  };
  trade?: {
    type: string;
    hash: string;
    eip712: {
      types: Record<string, any[]>;
      domain: Record<string, any>;
      message: Record<string, any>;
      primaryType: string;
    };
  };
  target?: string;
  zid?: string;
}

export interface GetFirmQuoteParams {
  sellToken: Omit<MultichainTokenBalance, "chainId"> & { chainId: ChainId };
  buyToken: Omit<MultichainTokenBalance, "chainId"> & { chainId: ChainId };
  sellAmount?: string;
  buyAmount?: string;
  takerAddress: string;
  network: BridgeNetwork;
  gasless?: boolean;
}

export async function getFirmQuote({
  sellToken,
  buyToken,
  sellAmount,
  buyAmount,
  takerAddress,
  network,
  gasless = false,
}: GetFirmQuoteParams): Promise<FirmQuoteResponse | null> {
  if (network === "shadownet") {
    throw new QuoteError(`Swapping is not supported on ${network} at the moment`);
  }

  // Only allow quotes for Ethereum mainnet
  if (sellToken.chainId !== ChainIds.ETHEREUM || buyToken.chainId !== ChainIds.ETHEREUM) {
    return null;
  }

  const params = new URLSearchParams({
    chainId: CHAIN_ID_MAPPING[sellToken.chainId as ChainId],
    sellToken: sellToken.address,
    buyToken: buyToken.address,
    taker: takerAddress,
    intentOnFilling: "true",
    gasless: gasless ? "true" : "false",
  });

  if (sellAmount) {
    // Convert to wei (18 decimals)
    const sellAmountWei = (parseFloat(sellAmount) * Math.pow(10, sellToken.decimals)).toString();

    if (isNaN(parseFloat(sellAmountWei))) {
      console.error("Invalid sell amount:", sellAmount);

      return null;
    }
    params.append("sellAmount", sellAmountWei);
  } else if (buyAmount) {
    // Convert to wei (18 decimals)
    const buyAmountWei = (parseFloat(buyAmount) * Math.pow(10, buyToken.decimals)).toString();

    if (isNaN(parseFloat(buyAmountWei))) {
      console.error("Invalid buy amount:", buyAmount);

      return null;
    }
    params.append("buyAmount", buyAmountWei);
  }

  try {
    const url = `${PRICE_SERVICE_URL}/0x-quote?${params.toString()}`;
    const response = await fetch(url);

    if (!response.ok) {
      console.error("Failed to fetch 0x firm quote:", await response.text());

      return null;
    }

    const data = await response.json();

    return data as FirmQuoteResponse;
  } catch (error) {
    console.error("Error fetching 0x firm quote:", error);

    return null;
  }
}
